import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Icon from "../components/Icon";
import { Button, Field } from "../components/ui";
import SignOff from "../components/SignOff";
import { useAuth } from "../auth";
import { apiGet } from "../api";
import { submitQueued } from "../offline";
import { getDeviceId } from "../deviceId";
import { getSticky, setSticky } from "../sticky";
import { C, R, S } from "../theme";

const label = { color: C.sub, fontSize: 13, fontWeight: 700, marginBottom: S.sm, textTransform: "uppercase" };

export default function TrainingNew() {
  const { aid } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [attraction, setAttraction] = useState(null);
  const [staff, setStaff] = useState("");
  const [trainer, setTrainer] = useState(user?.display_name || user?.company_name || "");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [outcome, setOutcome] = useState("competent");
  const [renewal, setRenewal] = useState("");
  const [loc, setLoc] = useState(getSticky("location"));
  const [lib, setLib] = useState([]);
  const [selected, setSelected] = useState([]); // topic names covered
  const [extra, setExtra] = useState([]); // ad-hoc topics added on the fly
  const [newTopic, setNewTopic] = useState("");
  const [trainerSign, setTrainerSign] = useState({ signature: null, name: user?.display_name || user?.company_name || "", initials: "", complete: false });
  const [traineeSign, setTraineeSign] = useState({ signature: null, name: "", initials: "", complete: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    apiGet(`/attractions/${aid}`).then(setAttraction).catch(() => {});
    apiGet(`/attractions/${aid}/training-topics`).then(setLib).catch(() => {});
  }, [aid]);

  const toggle = (name) => setSelected((s) => (s.includes(name) ? s.filter((x) => x !== name) : [...s, name]));
  const addExtra = () => {
    const n = newTopic.trim();
    if (!n) return;
    if (![...lib.map((t) => t.name), ...extra].includes(n)) setExtra((e) => [...e, n]);
    if (!selected.includes(n)) setSelected((s) => [...s, n]);
    setNewTopic("");
  };
  const removeExtra = (n) => { setExtra((e) => e.filter((x) => x !== n)); setSelected((s) => s.filter((x) => x !== n)); };

  const save = async () => {
    setErr("");
    if (!staff.trim() || !trainer.trim()) { setErr("Staff and trainer names are required"); return; }
    const topics = selected.filter(Boolean);
    if (topics.length === 0) { setErr("Select at least one training topic covered"); return; }
    if (!trainerSign.complete) { setErr("Trainer signature, printed name and initials are all required"); return; }
    if (!traineeSign.complete) { setErr("Trainee signature, printed name and initials are all required"); return; }
    setBusy(true);
    try {
      if (loc) setSticky("location", loc);
      const res = await submitQueued(`/attractions/${aid}/training`, {
        staff_name: staff, trainer_name: trainer, date_trained: date, topics,
        assessment_outcome: outcome || null, renewal_date: renewal || null, location: loc || null,
        signature: trainerSign.signature, signed_by_name: trainerSign.name, signed_by_initials: trainerSign.initials,
        trainee_signature: traineeSign.signature, trainee_name: traineeSign.name, trainee_initials: traineeSign.initials,
        device_id: getDeviceId(),
      });
      if (res?.result?.id) nav(`/training/${res.result.id}`, { replace: true }); else nav(-1);
    } catch (e) { setErr(e?.message || "Save failed"); } finally { setBusy(false); }
  };

  const outcomeBtn = (key, txt, color) => (
    <button data-testid={`train-outcome-${key}`} onClick={() => setOutcome(key)} style={{ flex: 1, background: outcome === key ? color : C.card2, borderRadius: R.md, padding: `${S.md}px 0`, color: outcome === key ? C.black : C.text, fontWeight: 800, border: `1px solid ${C.borderStrong}` }}>{txt}</button>
  );

  const allTopics = [...lib.map((t) => t.name), ...extra.filter((n) => !lib.some((t) => t.name === n))];

  return (
    <div className="fade-up">
      <div style={{ display: "flex", alignItems: "center", gap: S.sm, padding: S.lg }}>
        <button className="pressable" onClick={() => nav(-1)}><Icon name="chevron-back" size={28} color={C.text} /></button>
        <div className="display" style={{ color: C.text, fontSize: 20, fontWeight: 900 }}>Record Staff Training</div>
      </div>
      <div style={{ padding: S.lg }}>
        {attraction && <div style={{ color: C.muted, marginBottom: S.md }}>{attraction.name}</div>}
        <Field label="Staff member (trainee) name" testID="train-staff" value={staff} onChange={(e) => setStaff(e.target.value)} />
        <Field label="Trainer / assessor name" testID="train-trainer" value={trainer} onChange={(e) => setTrainer(e.target.value)} />
        <Field label="Date" testID="train-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        <Field label="Location / site (optional)" testID="train-location" value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="e.g. North field, Pitch 3" />

        <div style={label}>Training Topics Covered</div>
        <div data-testid="train-topics" style={{ marginBottom: S.sm }}>
          {allTopics.length === 0 && <div style={{ color: C.muted, fontSize: 13, marginBottom: S.sm }}>No topics yet — add one below.</div>}
          {allTopics.map((name) => {
            const on = selected.includes(name);
            const isExtra = extra.includes(name);
            return (
              <div key={name} data-testid={`topic-opt-${name}`} onClick={() => toggle(name)} className="pressable" style={{ display: "flex", alignItems: "center", gap: S.sm, background: on ? "rgba(16,185,129,0.12)" : C.card, borderRadius: R.md, padding: S.md, marginBottom: S.sm, border: `1px solid ${on ? C.green : C.border}`, cursor: "pointer" }}>
                <Icon name={on ? "checkmark-circle" : "ellipse-outline"} size={22} color={on ? C.green : C.muted} />
                <span style={{ flex: 1, color: C.text, fontWeight: 700, fontSize: 15 }}>{name}</span>
                {isExtra && <button onClick={(e) => { e.stopPropagation(); removeExtra(name); }} style={{ background: "transparent", border: "none", cursor: "pointer" }}><Icon name="close" size={18} color={C.muted} /></button>}
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: S.sm, marginBottom: S.md }}>
          <input data-testid="train-add-topic-input" value={newTopic} onChange={(e) => setNewTopic(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addExtra(); } }} placeholder="Add another topic covered…" style={{ flex: 1, background: C.card2, border: `1px solid ${C.borderStrong}`, borderRadius: R.md, color: C.text, fontSize: 15, minHeight: 48, padding: `0 ${S.md}px`, outline: "none", boxSizing: "border-box" }} />
          <button data-testid="train-add-topic-btn" className="pressable" onClick={addExtra} style={{ background: C.amber, color: C.black, fontWeight: 900, border: "none", borderRadius: R.md, padding: `0 ${S.lg}px`, minHeight: 48, cursor: "pointer" }}>Add</button>
        </div>

        <div style={label}>Assessment outcome</div>
        <div style={{ display: "flex", gap: S.sm, marginBottom: S.md }}>
          {outcomeBtn("competent", "Competent", C.green)}
          {outcomeBtn("needs_retraining", "Needs retraining", C.amber)}
        </div>
        <Field label="Renewal / next review date (optional)" testID="train-renewal" type="date" value={renewal} onChange={(e) => setRenewal(e.target.value)} />

        <div style={{ height: 1, background: C.border, margin: `${S.md}px 0` }} />
        <SignOff title="Trainer / Assessor Sign-Off" defaultName={trainer} onChange={setTrainerSign} />
        <div style={{ height: 1, background: C.border, margin: `${S.lg}px 0` }} />
        <SignOff title="Trainee Sign-Off" defaultName={staff} useSticky={false} persistSticky={false} onChange={setTraineeSign} />

        <div style={{ height: S.md }} />
        {!!err && <div style={{ color: C.red, fontWeight: 700, marginBottom: S.sm }}>{err}</div>}
        <Button title="Save Training Record" testID="train-save" variant="green" onPress={save} loading={busy} />
        <div style={{ height: S.xxl }} />
      </div>
    </div>
  );
}
